import { call, put, takeLatest, select, takeEvery } from 'typed-redux-saga';
import {
  getArticlePageData,
  articlePageFetchingStatus,
  getCategoryPageData,
  categoryPageFetchingStatus,
  getHomePageData,
  homePageFetchingStatus,
} from '../selectors';
import {
  sendArticlePageEvent,
  sendCategoryPageEvent,
  sendHomePageEvent,
} from './analytics';
import * as PageActions from '../actions/page';
import { fetchArticle, searchArticles } from '../api/article';
import { fetchCategory, fetchHome } from '../api/category';
import { ErrorStatus } from '../reducers/page';
import { GraphqlError } from '../utils/types';
import { getLanguageId } from '../utils/globals';
import { reportError } from '../bugTracking';

const getErrorStatus = (errors: GraphqlError[]): ErrorStatus => {
  if (checkErrorsForUnauth(errors)) {
    return 'NotAllowedError';
  }
  if (checkErrorsForNotFound(errors)) {
    return 'NotFound';
  }
  return 'HasError';
};

const checkErrorsForUnauth = (errors: GraphqlError[]) =>
  errors.some((err) => err?.extensions?.code === 'Unauthorized');

const checkErrorsForNotFound = (errors: GraphqlError[]) =>
  errors.some((err) => err?.extensions?.code === 'NotFound');

export function* requestArticlePageData(articleId: string, path: string) {
  try {
    if (!articleId) {
      throw new Error('Invalid article id');
    }

    // Check to see if we already have the data.
    const existingData = yield* select(getArticlePageData, articleId);
    const fetchingStatus = yield* select(articlePageFetchingStatus, articleId);

    // Don't try to reload if server has given these error types.
    if (fetchingStatus === 'LoginRequired') {
      return;
    }
    if (existingData) {
      // make sure we call article page view.
      yield* call(sendArticlePageEvent, articleId);
      return;
    }

    const languageId = yield* call(getLanguageId);
    const data = yield* call(fetchArticle, articleId, languageId);
    if (!data.article) {
      throw new Error('NotFound');
    }

    yield* put(
      PageActions.Actions.pageNavComplete({
        pageType: 'article',
        articleId,
        path,
        data,
      })
    );
  } catch (error) {
    if (!error.response) {
      yield* call(reportError, error);
    }
    const errors = error?.response?.errors || [];

    yield* put(
      PageActions.Actions.pageNavError({
        pageType: 'article',
        articleId,
        path,
        errorStatus: getErrorStatus(errors),
        errors,
      })
    );
  }
}

export function* requestCategoryPageData(categoryId: string, path: string) {
  try {
    if (!categoryId) {
      throw new Error('Invalid category id');
    }

    // Check to see if we already have the data.
    const existingData = yield* select(getCategoryPageData, categoryId);
    const fetchingStatus = yield* select(
      categoryPageFetchingStatus,
      categoryId
    );

    // Don't try to reload if server has given these error types.
    if (fetchingStatus === 'LoginRequired') {
      return;
    }

    if (existingData) {
      // make sure we call article page view.
      yield* call(sendCategoryPageEvent, categoryId);
      return;
    }

    const languageId = yield* call(getLanguageId);
    const data = yield* call(fetchCategory, categoryId, languageId);
    if (!data.category) {
      throw new Error('NotFound');
    }

    yield* put(
      PageActions.Actions.pageNavComplete({
        pageType: 'category',
        categoryId,
        path,
        data,
      })
    );
  } catch (error) {
    if (!error.response) {
      yield* call(reportError, error);
    }
    const errors = error?.response?.errors || [];
    yield* put(
      PageActions.Actions.pageNavError({
        pageType: 'category',
        categoryId,
        path,
        errorStatus: getErrorStatus(errors),
        errors,
      })
    );
  }
}

export function* requestHomePage(path: string) {
  // Check to see if we already have the data.
  const existingData = yield* select(getHomePageData);
  const fetchingStatus = yield* select(homePageFetchingStatus);

  // Don't try to reload if server has given these error types.
  if (fetchingStatus === 'LoginRequired') {
    return;
  }

  if (existingData) {
    // make sure we call article page view.
    yield* call(sendHomePageEvent);
    return;
  }

  try {
    const languageId = yield* call(getLanguageId);
    const data = yield* call(fetchHome, languageId);

    yield* put(
      PageActions.Actions.pageNavComplete({
        pageType: 'home',
        path,
        data,
      })
    );
  } catch (error) {
    if (!error.response) {
      yield* call(reportError, error);
    }
    const errors = error?.response?.errors || [];
    yield* put(
      PageActions.Actions.pageNavError({
        pageType: 'home',
        path,
        errorStatus: getErrorStatus(errors),
        errors,
      })
    );
  }
}

export function* requestSearchPage(query: string, path: string) {
  try {
    const languageId = yield* call(getLanguageId);
    const data = yield* call(searchArticles, query, languageId);
    yield* put(
      PageActions.Actions.pageNavComplete({
        pageType: 'search',
        path,
        searchTerm: query,
        data,
      })
    );
  } catch (error) {
    const errors = error?.response?.errors || [];
    yield* put(
      PageActions.Actions.pageNavError({
        pageType: 'search',
        searchTerm: query,
        path,
        errorStatus: getErrorStatus(errors),
        errors,
      })
    );
  }
}

export function* pageNavStart(
  action: ReturnType<typeof PageActions.Actions.pageNavStart>
) {
  const payload = action.payload;

  switch (payload.pageType) {
    case 'article':
      yield* call(requestArticlePageData, payload.articleId, payload.path);
      return;

    case 'category':
      yield* call(requestCategoryPageData, payload.categoryId, payload.path);
      return;

    case 'home':
      yield* call(requestHomePage, payload.path);
      return;

    case 'search':
      yield* call(requestSearchPage, payload.searchTerm, payload.path);
      return;

    default:
      throwPageType(payload);
  }
}

function throwPageType(p: never): never;
function throwPageType(p: any) {
  throw new Error('Unknown page type: ' + p);
}

async function ssoLogout() {
  window.location.href = `/sso/logout${window.location.search}`;
}

export function* checkUserAuth(
  action: ReturnType<typeof PageActions.Actions.pageNavError>
) {
  const isUnauth = checkErrorsForUnauth(action.payload.errors || []);

  if (!isUnauth) return;

  yield* call(ssoLogout);
}

export function* watchPageNav() {
  yield* takeLatest(PageActions.ActionTypes.PAGE_NAV_START, pageNavStart);
}

export function* watchPageNavErrors() {
  yield takeEvery(PageActions.ActionTypes.PAGE_NAV_ERROR, checkUserAuth);
}
